import { Button, Buttons, Flex, Icon, Image, Text } from '@bitrise/bitkit';
import React, { FunctionComponent } from 'react';

import dataAtIds from '../../../../../../dataAtIds.json';
import useDownloadConfigIOS from '../../../../../Settings/useDownloadConfigIOS';
import Guide from '../../Guide';
import GuideCode from '../../GuideCode';
import GuideStep from '../../GuideStep';
import GuideCocoaPodsStep3a from './GuideCocoaPods.3a.png';
import GuideCocoaPodsStep3b from './GuideCocoaPods.3b.png';
import GuideCocoaPodsStep4 from './GuideCocoaPods.4.png';

const GuideCocoaPods: FunctionComponent = () => {
  const handleDownloadCollectorToken = useDownloadConfigIOS();

  return (
    <Guide data-trace-at-id={dataAtIds.GettingStartedGuideIosCocoa} name="cocoapods">
      <GuideStep heading="Sample app" number="1">
        <Text>
          If you&apos;re not using your own app, start off by cloning the BookStore-iOS app in your
          terminal.
        </Text>

        <GuideCode bash copy>
          {`git clone git@github.com:bitrise-io/BookStore-iOS.git
cd BookStore-iOS/`}
        </GuideCode>
      </GuideStep>

      <GuideStep heading="Initialise SDK" number="2">
        <Text>Now we are going to install the Trace SDK via CocoaPods.</Text>

        <Text>1: Open the Podfile using your preferred editor</Text>

        <GuideCode bash copy>
          open -a TextEdit Podfile
        </GuideCode>

        <Text>2: Add the Trace SDK to your Podfile</Text>

        <GuideCode copy={[false, false, false, true]}>
          {`# Podfile

target 'YOUR_TARGET_NAME' do
  pod 'BitriseTrace'
end `}
        </GuideCode>

        <Text>3: Save and close the Podfile</Text>

        <Text>4: Install the Trace SDK</Text>

        <GuideCode bash copy>
          pod install
        </GuideCode>

        <Text>
          That should result in a{' '}
          <Text inline weight="bold">
            Pod installation complete!
          </Text>{' '}
          message. If there were any errors please investigate before proceeding.
        </Text>
      </GuideStep>

      <GuideStep heading="Download Config file" number="3">
        <Text>
          One last thing is to add the{' '}
          <Text inline weight="bold">
            bitrise_configuration.plist
          </Text>{' '}
          to your project
        </Text>

        <Flex alignChildren="middle" direction="horizontal" gap="x3">
          <Flex grow initial="none">
            <Text>
              1: Download{' '}
              <Text inline weight="bold">
                bitrise_configuration.plist
              </Text>
            </Text>
          </Flex>

          <Flex>
            <Buttons>
              <Button elevation="x1" onClick={handleDownloadCollectorToken} size="small">
                <Icon name="Download" size="1rem" />
                <Text>Download</Text>
              </Button>
            </Buttons>
          </Flex>
        </Flex>

        <Text>2: Open your project in XCode</Text>

        <GuideCode bash copy>
          open BookStore.xcworkspace/
        </GuideCode>

        <Text>
          3: Now, drag and drop the bitrise_configuration.plist to the root of your project, in this
          case it directly into the BookStore project
        </Text>

        <Image borderRadius="x2" elevation="x1" src={GuideCocoaPodsStep3a} width="251px" />
        <Text>4: If prompted, confirm to add the BookStore project</Text>

        <Image borderRadius="x2" elevation="x1" src={GuideCocoaPodsStep3b} width="600px" />

        <Text>5: All done! 🙌</Text>
      </GuideStep>

      <GuideStep heading="Now you can run it!" number="4">
        <Text>All that is left now is to build and run the app in the simulator or a device.</Text>

        <Image borderRadius="x2" elevation="x1" src={GuideCocoaPodsStep4} width="600px" />

        <Text>
          Check for any errors in the logs. Please retry the setup, or contact support if you get
          any of the following errors:
        </Text>

        <GuideCode>
          {`[Bitrise:Trace/internalError] Bitrise configuration file is missing from Bundle.main
[Bitrise:Trace/internalError] Application failed to read configuration file
[Bitrise:Trace/queue] Failed to submit metric, will try again in 1 minute.`}
        </GuideCode>

        <Text>
          Provided there are no errors, your app will be reporting performance data. Feel free to
          play around the app to generate more sample data.
        </Text>
      </GuideStep>
    </Guide>
  );
};

export default GuideCocoaPods;
